import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import UserContext from '../context/UserContext'
import { MY_TEACHER_LINKS_QUERY, SHARE_DRILL_RESULT_MUTATION } from '../query/gqlQueries'
import languages from '../languages'
import styles from './ShareWithTeacher.module.scss'

// Send a finished practice set to a teacher. Used by Special Mode and Verb Lab.
//
// Only teachers ticked for this set's language are offered: a Russian set goes
// to your Russian teacher, not to whoever teaches you Turkish. There is no
// free-text username box — that would let anyone probe for other accounts.
//
// Sending is always the learner's decision, set by set. `buildDetails` is only
// called when they press Share, so nothing is serialised for a set never sent.
export default function ShareWithTeacher({ title, langCode, mode, total, perfect, buildDetails }) {
  const { username } = useContext(UserContext)
  const { data, loading: loadingLinks } = useQuery(MY_TEACHER_LINKS_QUERY, { variables: {}, skip: !username, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [shareResult, { loading: sending }] = useMutation(SHARE_DRILL_RESULT_MUTATION)
  const lang = String(langCode || '').toLowerCase()
  const links = useMemo(() => data?.myTeacherLinks || [], [data])
  const eligible = useMemo(
    () => links.filter(l => (l.langs || []).includes(lang)).map(l => l.username),
    [links, lang]
  )
  const [to, setTo] = useState('')
  const [sentTo, setSentTo] = useState([])
  const [err, setErr] = useState('')
  useEffect(() => { setTo(t => (eligible.includes(t) ? t : (eligible[0] || ''))) }, [eligible])

  if (!username) return null
  const langName = languages[lang] || lang || 'this language'
  const alreadySent = sentTo.includes(to)

  const send = async () => {
    if (!to || alreadySent) return
    setErr('')
    try {
      const details = JSON.stringify(buildDetails ? buildDetails() : [])
      await shareResult({ variables: { teacher: to, input: { title: title || 'Practice set', language: lang || null, mode, total, perfect, details } } })
      setSentTo(s => (s.includes(to) ? s : [...s, to]))
    } catch (e) { setErr(e.message || 'Could not share.') }
  }

  return (
    <div className={styles.box}>
      <div className={styles.head}>Share with your teacher</div>
      {loadingLinks && links.length === 0 ? (
        <div className={styles.note}>Checking your teachers…</div>
      ) : links.length === 0 ? (
        <div className={styles.note}>You have not added a teacher yet. Ask yours for their teaching code, then enter it under <strong>Homework → My teachers</strong>.</div>
      ) : eligible.length === 0 ? (
        <div className={styles.note}>None of your teachers is set up for {langName}. Tick {langName} for the right teacher under <strong>Homework → My teachers</strong>, then share.</div>
      ) : (
        <>
          {eligible.length > 1 && (
            <div className={styles.chips}>
              {eligible.map(t => (
                <button key={t} className={`${styles.chip} ${to === t ? styles.chipOn : ''}`} onClick={() => setTo(t)}>{t}</button>
              ))}
            </div>
          )}
          <div className={styles.row}>
            <button className={styles.btn} onClick={send} disabled={sending || !to || alreadySent}>
              {sending ? 'Sending…' : alreadySent ? `Sent to ${to} ✓` : `Share with ${to} →`}
            </button>
          </div>
          <div className={styles.why}>They see your score and the items that needed help — not the whole session.</div>
        </>
      )}
      {err && <div className={styles.err}>{err}</div>}
    </div>
  )
}
